import { Button } from "@/components/ui/button"
import type { MemorySavedLine } from "@/lib/memory-saved"

interface MemorySavedLinesProps {
  lines: MemorySavedLine[] | undefined
  onUndo: (memoryId: string) => void
}

/**
 * "Saved to memory · Undo" under an AI reply — one quiet line per item the AI
 * saved. Shared by the editor panel, Tender Chat and the Assistant. An item
 * someone already removed reads "Already removed", never as an error.
 */
export function MemorySavedLines({ lines, onUndo }: MemorySavedLinesProps) {
  if (!lines || lines.length === 0) return null
  return (
    <div className="mt-1 flex flex-col gap-0.5" data-testid="memory-saved-lines">
      {lines.map((line) => (
        <MemorySavedLineRow key={line.memoryId} line={line} onUndo={onUndo} />
      ))}
    </div>
  )
}

function MemorySavedLineRow({ line, onUndo }: { line: MemorySavedLine; onUndo: (memoryId: string) => void }) {
  const undoButton = (label: string) => (
    <Button
      type="button"
      variant="link"
      className="h-auto p-0 text-xs"
      disabled={line.status === "undoing"}
      onClick={() => onUndo(line.memoryId)}
    >
      {label}
    </Button>
  )

  if (line.status === "undone") {
    return <p className="text-meta" role="status">Removed from memory</p>
  }
  if (line.status === "already_removed") {
    return <p className="text-meta" role="status">Already removed</p>
  }
  if (line.status === "undo_failed") {
    return (
      <p className="text-meta flex items-center gap-1" role="status">
        <span>Couldn&rsquo;t remove it from memory</span>
        <span aria-hidden="true">·</span>
        {undoButton("Try again")}
      </p>
    )
  }
  return (
    <p className="text-meta flex items-center gap-1" title={line.content}>
      <span>Saved to memory</span>
      <span aria-hidden="true">·</span>
      {undoButton("Undo")}
    </p>
  )
}
